import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Loader2 } from "lucide-react";

const DEFAULT_RECIPIENT_EMAIL = "info@hvkurz.de";

export const EmailDialog = ({ isOpen, onClose, onSend, isLoading, description, date, documentId }) => {
  const [email, setEmail] = useState(DEFAULT_RECIPIENT_EMAIL);
  const defaultSubject = `Dokumentation Objektbegehung - ${description || ''} ${date || ''}`.trim();
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("Anbei erhalten Sie die Dokumentation zur Objektbegehung.");
  const [includeEditLink, setIncludeEditLink] = useState(false);

  useEffect(() => {
    const newSubject = `Dokumentation Objektbegehung - ${description || ''} ${date || ''}`.trim();
    setSubject(newSubject);
  }, [description, date]);

  useEffect(() => {
    if (isOpen && !email) {
      setEmail(DEFAULT_RECIPIENT_EMAIL);
    }
  }, [isOpen, email]);

  const handleSend = () => {
    if (!email || !email.includes("@")) {
      return;
    }
    let finalMessage = message;
    if (includeEditLink && documentId) {
      const editLink = `${window.location.origin}/dokument/${documentId}`;
      finalMessage = `${message}\n\nBearbeitungslink: ${editLink}`;
    }
    onSend({ email, subject, message: finalMessage });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="email-modal">
        <DialogHeader>
          <DialogTitle>PDF per E-Mail senden</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="email-to">Empfänger E-Mail *</Label>
            <Input
              id="email-to"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="empfaenger@beispiel.de"
              data-testid="email-input"
            />
          </div>
          <div>
            <Label htmlFor="email-subject">Betreff</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Betreff"
              data-testid="email-subject"
            />
          </div>
          <div>
            <Label htmlFor="email-message">Nachricht</Label>
            <Textarea
              id="email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ihre Nachricht..."
              rows={3}
              data-testid="email-message"
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="include-edit-link"
              checked={includeEditLink}
              onChange={(e) => setIncludeEditLink(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
              data-testid="include-edit-link-checkbox"
            />
            <Label htmlFor="include-edit-link" className="text-sm cursor-pointer">
              Bearbeitungslink zur Datei in E-Mail einfügen
            </Label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="email-cancel-btn">
            Abbrechen
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={!email || !email.includes("@") || isLoading}
            data-testid="email-send-btn"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            Senden
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDialog;
